import type { IRule } from '@/types/filterBuilderTypes';
import { buildEntityColumns } from '@/composables/entityColumns';
import { extraFilterColumns } from '@/components/FilterBuilder/filterControlRegistry';
import { columnHeaderLabel } from '@/utils/columnLabels';

/** The filter builder's words for each criterion, as a chip shows them. */
const CRITERION_WORDS: Record<string, string> = {
    equals: 'is',
    not_equals: 'is not',
    greater_than: 'is over',
    less_than: 'is under',
    contains: 'contains',
    not_contains: 'does not contain',
    contains_any: 'is one of',
    not_contains_any: 'is none of',
    contains_all: 'contains all',
    not_contains_all: 'does not contain all',
    starts_with: 'starts with',
    is_empty_or_null: 'is empty',
    is_not_empty_or_null: 'is not empty',
    between: 'is between',
    is: 'is',
};

function describeValue(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
        return value.map((item) => describeValue(item)).join(', ');
    }
    return String(value);
}

/**
 * One filter rule in words, for a chip: the column's header, the criterion and
 * the value. For example `{ column: 'location_zone', field: 'display_string',
 * criterion: 'contains', value: 'rau' }` reads "Location Zone contains rau".
 */
export function describeFilterRule(entityId: string, rule: IRule): { column: string; criterion: string; value: string } {
    const key = rule.field ? `${rule.column}.${rule.field}` : rule.column;
    // A filter-only column, such as a custom list's "Silence" in the Schedule group, comes
    // first. Otherwise its rule matches the "schedules" table column by id and reads "Schedules".
    const column =
        extraFilterColumns[entityId]?.find((candidate) => candidate.key === key) ??
        buildEntityColumns(entityId).find((candidate) => candidate.key === key || candidate.id === rule.column);
    return {
        column: column?.header ?? columnHeaderLabel(rule.column),
        criterion: CRITERION_WORDS[rule.criterion] ?? rule.criterion.replace(/_/g, ' '),
        value: describeValue(rule.value),
    };
}
